import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
`;

const Card = styled.div`
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  max-width: 400px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #334155;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h2`
  color: #f1f5f9;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const Message = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  line-height: 1.6;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid rgba(239, 68, 68, 0.2);
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  margin-top: 1.5rem;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.35);
  }
`;

const SsoCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const tenantCode = searchParams.get('tenantCode');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage(decodeURIComponent(error));
        return;
      }

      if (!token) {
        setStatus('error');
        setErrorMessage('No authentication token received');
        return;
      }

      try {
        // Tenant info is in the JWT token - no need to store separately

        // Decode JWT to get user info (simple decode, no verification - that's done server-side)
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        const user = {
          id: payload.userId || 0,
          username: payload.sub,
          fullName: payload.fullName || payload.sub,
          email: payload.email || '',
          role: payload.role || 'USER',
        };

        // Use loginWithToken to set auth state
        loginWithToken(token, user);
        
        setStatus('success');
        
        // Redirect after a brief delay
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err.message || 'Failed to process SSO login');
      }
    };

    handleCallback();
  }, [searchParams, loginWithToken]);

  const handleRetry = () => {
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <Container>
        <Card>
          <Spinner />
          <Title>Completing Sign In</Title>
          <Message>Please wait while we verify your identity...</Message>
        </Card>
      </Container>
    );
  }

  if (status === 'success') {
    return (
      <Container>
        <Card>
          <ErrorIcon>✅</ErrorIcon>
          <Title>Sign In Successful!</Title>
          <Message>Redirecting you to the dashboard...</Message>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <ErrorIcon>❌</ErrorIcon>
        <Title>Sign In Failed</Title>
        <Message>We couldn't complete your SSO sign in.</Message>
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <Button onClick={handleRetry}>Back to Login</Button>
      </Card>
    </Container>
  );
};

export default SsoCallback;
